<template>
  <!-- As perguntas na ordem em que o servidor as avalia — a mesma em que cada uma pode ler a resposta
       das anteriores (`_visible` em "Editável quando"). -->
  <div>
    <p class="text-body-2 text-medium-emphasis mb-4"><ontos-code-text :text="explain || $tt('access.explain')"/></p>
    <template v-for="kind of kinds" :key="kind">
      <!-- Sem fórmula, o botão: todos podem, e o editor só vem para quem o pede — o arranjo das
           Opções permitidas (pedido do usuário: o campo vazio para tirar a fórmula não dizia nada).
           Travado, o botão diz o porquê embaixo — o padrão do par travado -->
      <template v-if="value[kind] === undefined">
        <v-btn variant="text" class="d-flex" :class="{'mb-2': !blocked[kind]}" :disabled="!!blocked[kind]"
               :prepend-icon="icons[kind]" @click="change(kind, '')">
          {{ $tt(labels[kind]) }}
        </v-btn>
        <div v-if="blocked[kind]" class="text-body-2 text-medium-emphasis mb-2 ml-4">
          <v-icon size="small" start>mdi-lock</v-icon>{{ blocked[kind] }}
        </div>
      </template>
      <!-- O arranjo da linha das Opções permitidas: a fórmula e o remover -->
      <v-row v-else dense align="start" class="mb-2">
        <v-col cols="10" md="11">
          <ontos-formula-input :ref="kind" class="required" :label="$tt(labels[kind])"
                               :access="draft ? undefined : kind" :field-access="draft ? kind : undefined"
                               :draft="draft || {}" :current="current"
                               :value="value[kind]" :example="examples[kind]" :result-label="resultLabel"
                               :system-alias="workspace.alias" :idea="idea"
                               :choice-domains="workspace.domains"
                               @input="change(kind, $event)" @validity="validity(kind, $event)"/>
        </v-col>
        <v-col cols="2" md="1">
          <v-btn icon variant="text" size="small" :title="$tt('remove')" @click="remove(kind)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </div>
</template>

<script lang="ts">
import {Component, Prop, Vue, toNative} from 'vue-facing-decorator'
import AsyncFormulaInput from '@/components/design/property/asyncFormulaInput'
import CodeText from '@/components/shared/CodeText.vue'
import Idea, {ACCESS_ICONS, ACCESS_KINDS, ACCESS_LABELS, AccessKind, IdeaAccess} from '@/types/Idea'
import IdeaProperty from '@/types/IdeaProperty'
import {EnhancedWorkpace} from '@/types/Workspace'
import {accessExamples, fieldAccessExamples} from '@/utils/formulaExamples'
import types from '@/model/types'

/**
 * Quem vê, edita, exclui e cria os itens de uma coleção — uma fórmula de Sim/Não por pergunta, no
 * design (pedido do usuário). O mesmo editor de fórmula das calculadas e das regras, no modo de
 * acesso: Sim/Não, sem atributo.
 *
 * E quem vê e quem edita um CAMPO (pedido do usuário): as mesmas perguntas, só as duas (`kinds`), com o
 * rascunho da propriedade (`draft`) — a fórmula vai dentro dele, e o veredito e as pessoas vêm como os
 * da coleção. Um editor para os dois: eram as mesmas linhas, o mesmo botão, o mesmo remover.
 *
 * Sem fórmula é "todos podem", e a pergunta é um botão; com ela, a linha das Opções permitidas — o
 * editor obrigatório e o remover. `undefined` é não ter a fórmula, e o texto vazio, a que se começou
 * a escrever.
 *
 * Emite o acesso INTEIRO a cada mudança: o servidor grava o acesso todo de uma vez, e uma mudança
 * parcial apagaria as outras perguntas. E `validity` com todas juntas, para quem salva.
 */
@Component({
  name: 'ontos-access-input',
  emits: ['input', 'validity'],
  components: {'ontos-formula-input': AsyncFormulaInput, 'ontos-code-text': CodeText}
})
class AccessInput extends Vue {
  @Prop({type: Object, default: () => ({})}) readonly value!: IdeaAccess
  /** A coleção como está gravada: é dela que o editor tira o vocabulário. */
  @Prop({type: Object, required: true}) readonly idea!: Idea
  @Prop({type: Object, required: true}) readonly workspace!: EnhancedWorkpace
  /** As perguntas: as quatro da coleção, ou as do campo (`PROPERTY_ACCESS_KINDS`). */
  @Prop({type: Array, default: () => ACCESS_KINDS}) readonly kinds!: readonly AccessKind[]
  /** O rascunho da propriedade, no acesso de um campo; sem ele, é o da coleção. */
  @Prop({type: Object, default: undefined}) readonly draft?: Partial<IdeaProperty>
  /** O alias da propriedade em edição, que a checagem troca pelo rascunho. */
  @Prop({type: String, default: undefined}) readonly current?: string
  /** A frase de cima; sem ela, a do acesso da coleção. */
  @Prop({type: String, default: ''}) readonly explain!: string
  /** Por que uma pergunta não se oferece — o "Visível quando" do campo do identificador —, por pergunta. */
  @Prop({type: Object, default: () => ({})}) readonly blocked!: Partial<Record<AccessKind, string>>

  labels = ACCESS_LABELS
  icons = ACCESS_ICONS
  //Exposto ao template: a fórmula de acesso dá Sim/Não, o nome que o app dá ao tipo
  resultLabel = types.boolean.name
  /** As perguntas cuja fórmula a última checagem recusou */
  invalid: AccessKind[] = []

  /** Um exemplo por pergunta, com os nomes desta coleção — a regra mora no `formulaExamples`. */
  get examples(): Partial<Record<AccessKind, string>> {
    return this.draft ? fieldAccessExamples(this.draft, this.idea, this.workspace) : accessExamples(this.idea, this.workspace)
  }

  change(kind: AccessKind, source: string | undefined) {
    const access = {...this.value}
    if (source === undefined) delete access[kind]
    else access[kind] = source
    this.$emit('input', access)
  }

  /** Sem a fórmula, sem a recusa dela. */
  remove(kind: AccessKind) {
    this.validity(kind, true)
    this.change(kind, undefined)
  }

  validity(kind: AccessKind, valid: boolean) {
    this.invalid = valid ? this.invalid.filter(other => other !== kind) : [...new Set([...this.invalid, kind])]
    this.$emit('validity', this.invalid.length === 0)
  }

  /**
   * Na hora de salvar, como o editor das regras: toda fórmula começada e não conferida acusa, e a vazia
   * diz que é obrigatória — a linha existe para ela.
   */
  validate(): boolean {
    const editors = this.kinds
        .filter(kind => this.value[kind] !== undefined)
        .map(kind => [this.$refs[kind]].flat()[0] as { touch(): void } | undefined)
    editors.forEach(editor => editor && editor.touch())
    return this.invalid.length === 0 && this.kinds.every(kind => this.value[kind] === undefined || !!this.value[kind]!.trim())
  }
}

export default toNative(AccessInput)
</script>
